// src/components/ui/DevicePreview.tsx

'use client'

import { type ReactNode, useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiCheckLine from "~/icons/remix/generated/RiCheckLine"
import { cn } from '~/lib/utils'

/**
 * Represents an aspect ratio configuration for device preview.
 * 
 * This interface defines the structure for aspect ratio options that can be
 * selected in the device preview component, including display information
 * and recommendation status.
 *
 * @interface AspectRatio
 * @property {string} ratio - Display name for the aspect ratio (e.g., "16:9", "4:3")
 * @property {number} width - Width in pixels - must be positive integer
 * @property {number} height - Height in pixels - must be positive integer
 * @property {boolean} [recommended] - Whether this ratio is recommended for the use case
 * @property {string} [id] - Unique identifier for the aspect ratio - auto-generated if not provided
 * @example
 * // Standard aspect ratio configuration
 * const aspectRatio: AspectRatio = {
 *   ratio: "16:9",
 *   width: 1920,
 *   height: 1080,
 *   recommended: true,
 *   id: "16-9-hd"
 * }
 */
interface AspectRatio {
  /** Display name for the aspect ratio (e.g., "16:9", "4:3") */
  ratio: string
  /** Width in pixels */
  width: number
  /** Height in pixels */
  height: number
  /** Whether this ratio is recommended for the use case */
  recommended?: boolean
  /** Unique identifier for the aspect ratio */
  id?: string
}

/**
 * Type-only fallback ratio. Used solely to give `getInitialRatio` a
 * non-undefined return for the type-checker; it is never reached at runtime
 * because the `DevicePreview` wrapper guarantees a non-empty `aspectRatios`.
 */
const FALLBACK_ASPECT_RATIO: AspectRatio = { ratio: '1:1', width: 1, height: 1 }

/**
 * Safe zone configuration as percentages of the content area.
 * 
 * All values should be between 0 and 1, representing the percentage
 * of the content area that should be considered "safe" from UI elements
 * like notches, home indicators, or system UI overlays.
 *
 * @interface SafeZones
 * @property {number} top - Top safe zone as percentage (0-1) - area to avoid at top of screen
 * @property {number} bottom - Bottom safe zone as percentage (0-1) - area to avoid at bottom of screen
 * @property {number} left - Left safe zone as percentage (0-1) - area to avoid on left side of screen
 * @property {number} right - Right safe zone as percentage (0-1) - area to avoid on right side of screen
 */
interface SafeZones {
  /** Top safe zone as percentage (0-1) */
  top: number
  /** Bottom safe zone as percentage (0-1) */
  bottom: number
  /** Left safe zone as percentage (0-1) */
  left: number
  /** Right safe zone as percentage (0-1) */
  right: number
}

/**
 * Props for the DevicePreview component.
 * 
 * This interface defines all the configuration options for the DevicePreview
 * component, supporting both controlled and uncontrolled usage patterns.
 *
 * @interface DevicePreviewProps
 * @property {'mobile' | 'desktop'} deviceType - Type of device to simulate
 * @property {AspectRatio[]} aspectRatios - Available aspect ratios for preview
 * @property {SafeZones} [safeZones] - Safe zone configuration
 * @property {boolean} [showSafeZones=true] - Whether to show safe zone overlays
 * @property {string} [className] - Additional CSS classes
 * @property {ReactNode} [children] - Content to render inside the device preview
 * @property {AspectRatio} [selectedRatio] - Currently selected aspect ratio (controlled)
 * @property {Function} [onRatioChange] - Callback when aspect ratio changes
 *   @property {AspectRatio} onRatioChange.ratio - The newly selected aspect ratio
 * @property {boolean} [showRatioSelector] - Whether the ratio selector is open (controlled)
 * @property {Function} [onRatioSelectorToggle] - Callback when ratio selector visibility changes
 *   @property {boolean} onRatioSelectorToggle.show - Whether the selector should be shown
 * @property {boolean} [showRatioSelectorButton=true] - Whether to show the aspect ratio selector button
 * @property {string} [deviceLabel] - Custom device label
 * @property {boolean} [showDeviceUI=true] - Whether to show device UI elements (notch, home indicator)
 * @property {number} [animationDuration=0.2] - Animation duration for transitions in seconds
 * @example
 * // Controlled usage with external state management
 * <DevicePreview
 *   deviceType="mobile"
 *   aspectRatios={aspectRatios}
 *   selectedRatio={currentRatio}
 *   onRatioChange={setCurrentRatio}
 *   showRatioSelector={selectorOpen}
 *   onRatioSelectorToggle={setSelectorOpen}
 *   safeZones={{ top: 0.1, bottom: 0.1, left: 0.05, right: 0.05 }}
 * >
 *   <YourContent />
 * </DevicePreview>
 */
interface DevicePreviewProps {
  /** Type of device to simulate */
  deviceType: 'mobile' | 'desktop'
  /** Available aspect ratios for preview */
  aspectRatios: AspectRatio[]
  /** Safe zone configuration */
  safeZones?: SafeZones
  /** Whether to show safe zone overlays */
  showSafeZones?: boolean
  /** Additional CSS classes */
  className?: string
  /** Content to render inside the device preview */
  children?: ReactNode
  /** Currently selected aspect ratio (controlled) */
  selectedRatio?: AspectRatio
  /** Callback when aspect ratio changes */
  onRatioChange?: (ratio: AspectRatio) => void
  /** Whether the ratio selector is open (controlled) */
  showRatioSelector?: boolean
  /** Callback when ratio selector visibility changes */
  onRatioSelectorToggle?: (show: boolean) => void
  /** Whether to show the aspect ratio selector button */
  showRatioSelectorButton?: boolean
  /** Custom device label */
  deviceLabel?: string
  /** Whether to show device UI elements (notch, home indicator) */
  showDeviceUI?: boolean
  /** Animation duration for transitions */
  animationDuration?: number
}

/**
 * Mobile device physical dimensions configuration.
 *
 * @interface MobileDevice
 * @property {number} width - Device width in pixels
 * @property {number} height - Device height in pixels
 * @property {number} bezelWidth - Width of device bezel in pixels
 * @property {number} notchHeight - Height of device notch in pixels
 * @property {number} cornerRadius - Corner radius for device edges in pixels
 */
interface MobileDevice {
  width: number
  height: number
  bezelWidth: number
  notchHeight: number
  cornerRadius: number
}

/**
 * Desktop device physical dimensions configuration.
 *
 * @interface DesktopDevice
 * @property {number} width - Display width in pixels
 * @property {number} height - Display height in pixels
 * @property {number} bezelWidth - Width of display bezel in pixels
 * @property {number} cornerRadius - Corner radius for display edges in pixels
 */
interface DesktopDevice {
  width: number
  height: number
  bezelWidth: number
  cornerRadius: number
}

/**
 * Device dimension configurations for different device types.
 * 
 * Provides predefined dimensions for mobile and desktop devices
 * used in the preview component rendering.
 *
 * @typedef {Object} DeviceDimensions
 * @property {MobileDevice} mobile - Mobile device dimensions
 * @property {DesktopDevice} desktop - Desktop device dimensions
 */
const deviceDimensions: {
  mobile: MobileDevice
  desktop: DesktopDevice
} = {
  mobile: {
    width: 375,
    height: 812,
    bezelWidth: 24,
    notchHeight: 34,
    cornerRadius: 44
  },
  desktop: {
    width: 1440,
    height: 900,
    bezelWidth: 16,
    cornerRadius: 8
  }
} as const

/**
 * DevicePreview component provides a realistic device mockup for previewing content
 * at different aspect ratios with optional safe zone indicators.
 * 
 * This component supports both controlled and uncontrolled usage patterns for integration
 * with external state management. It automatically handles responsive scaling and
 * provides visual feedback for safe zones and device UI elements.
 * 
 * @param {Object} props - The component props
 *   @param {'mobile' | 'desktop'} props.deviceType - Type of device to simulate
 *   @param {AspectRatio[]} props.aspectRatios - Available aspect ratios for preview
 *   @param {SafeZones} [props.safeZones] - Safe zone configuration
 *   @param {boolean} [props.showSafeZones=true] - Whether to show safe zone overlays
 *   @param {string} [props.className] - Additional CSS classes
 *   @param {ReactNode} [props.children] - Content to render inside the device preview
 *   @param {AspectRatio} [props.selectedRatio] - Currently selected aspect ratio (controlled)
 *   @param {Function} [props.onRatioChange] - Callback when aspect ratio changes
 *   @param {boolean} [props.showRatioSelector] - Whether the ratio selector is open (controlled)
 *   @param {Function} [props.onRatioSelectorToggle] - Callback when ratio selector visibility changes
 *   @param {boolean} [props.showRatioSelectorButton=true] - Whether to show the aspect ratio selector button
 *   @param {string} [props.deviceLabel] - Custom device label
 *   @param {boolean} [props.showDeviceUI=true] - Whether to show device UI elements
 *   @param {number} [props.animationDuration=0.2] - Animation duration for transitions
 * @returns {JSX.Element} The rendered device preview component
 * @throws {Error} When aspectRatios array is empty
 * @example
 * ```tsx
 * // Uncontrolled usage
 * <DevicePreview
 *   deviceType="mobile"
 *   aspectRatios={[
 *     { ratio: "16:9", width: 1920, height: 1080, recommended: true },
 *     { ratio: "4:3", width: 1024, height: 768 }
 *   ]}
 *   safeZones={{ top: 0.1, bottom: 0.1, left: 0.05, right: 0.05 }}
 * >
 *   <YourContent />
 * </DevicePreview>
 * 
 * // Controlled usage
 * <DevicePreview
 *   deviceType="mobile"
 *   aspectRatios={aspectRatios}
 *   selectedRatio={currentRatio}
 *   onRatioChange={setCurrentRatio}
 *   showRatioSelector={selectorOpen}
 *   onRatioSelectorToggle={setSelectorOpen}
 * >
 *   <YourContent />
 * </DevicePreview>
 * ```
 */
/**
 * Guard wrapper: renders a graceful fallback when no aspect ratios are provided
 * instead of throwing during render (which, with no error boundary around
 * callers, would crash the React tree). All hooks live in `DevicePreviewInner`,
 * which only mounts once `aspectRatios` is non-empty — so this never violates
 * the Rules of Hooks.
 */
export function DevicePreview(props: DevicePreviewProps): JSX.Element {
  if (props.aspectRatios.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400',
          props.className
        )}
        role="status"
      >
        No aspect ratios available to preview.
      </div>
    )
  }
  return <DevicePreviewInner {...props} />
}

function DevicePreviewInner({
  deviceType,
  aspectRatios,
  safeZones,
  showSafeZones = true,
  className,
  children,
  selectedRatio: controlledSelectedRatio,
  onRatioChange,
  showRatioSelector: controlledShowRatioSelector,
  onRatioSelectorToggle,
  showRatioSelectorButton = true,
  deviceLabel,
  showDeviceUI = true,
  animationDuration = 0.2
}: DevicePreviewProps): JSX.Element {
  // `aspectRatios` is guaranteed non-empty here — the exported `DevicePreview`
  // wrapper renders a fallback for the empty case, so this component never has
  // to throw during render.

  // Container ref for responsive sizing
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // Internal state for uncontrolled usage - provides fallback when no external state is provided
  // We prioritize recommended ratios, falling back to the first available ratio
  const getInitialRatio = useCallback((): AspectRatio => {
    const recommendedRatio = aspectRatios.find(r => r.recommended)
    // `aspectRatios` is non-empty (guaranteed by the wrapper), so `[0]` is
    // defined; `FALLBACK_ASPECT_RATIO` only satisfies the type-checker and is
    // never reached at runtime.
    return recommendedRatio ?? aspectRatios[0] ?? FALLBACK_ASPECT_RATIO
  }, [aspectRatios])

  const [internalSelectedRatio, setInternalSelectedRatio] = useState<AspectRatio>(getInitialRatio)
  const [internalShowRatioSelector, setInternalShowRatioSelector] = useState(false)

  // Determine if component is controlled or uncontrolled for state management
  const isControlled = controlledSelectedRatio !== undefined
  const isSelectorControlled = controlledShowRatioSelector !== undefined

  // Use controlled or uncontrolled values based on prop availability
  // This ensures type safety by providing guaranteed non-undefined values
  const selectedRatio: AspectRatio = isControlled ? controlledSelectedRatio : internalSelectedRatio
  const showRatioSelectorState = isSelectorControlled ? controlledShowRatioSelector : internalShowRatioSelector

  // Handle container resize for responsive scaling
  // Uses ResizeObserver for efficient resize detection
  useEffect((): (() => void) => {
    const updateWidth = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    // Initial width calculation
    updateWidth()
    
    // Set up resize observer for dynamic width updates
    const resizeObserver = new ResizeObserver(updateWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return (): void => {
      resizeObserver.disconnect()
    }
  }, [])

  // Handle ratio selection
  const handleRatioChange = useCallback((ratio: AspectRatio): void => {
    if (isControlled) {
      // In controlled mode, delegate to parent component
      onRatioChange?.(ratio)
    } else {
      // In uncontrolled mode, manage state internally
      setInternalSelectedRatio(ratio)
    }
  }, [isControlled, onRatioChange])

  // Handle ratio selector toggle with proper controlled/uncontrolled logic
  const handleRatioSelectorToggle = useCallback((show: boolean): void => {
    if (isSelectorControlled) {
      // In controlled mode, delegate to parent component
      onRatioSelectorToggle?.(show)
    } else {
      // In uncontrolled mode, manage state internally
      setInternalShowRatioSelector(show)
    }
  }, [isSelectorControlled, onRatioSelectorToggle])

  // Handle ratio selector button click - toggles current state
  const handleSelectorButtonClick = useCallback((): void => {
    handleRatioSelectorToggle(!showRatioSelectorState)
  }, [handleRatioSelectorToggle, showRatioSelectorState])

  // Handle ratio selection and close selector
  const handleRatioSelect = useCallback((ratio: AspectRatio): void => {
    handleRatioChange(ratio)
    handleRatioSelectorToggle(false)
  }, [handleRatioChange, handleRatioSelectorToggle])

  // Get device configuration
  const device = deviceDimensions[deviceType]
  
  // Calculate device scale to fit container while maintaining aspect ratio
  // Prevents device from overflowing container width
  const deviceScale = containerWidth > 0 ? containerWidth / device.width : 1
  
  // Calculate content dimensions to fit within device boundaries
  // Account for device-specific UI elements like notches
  const notchHeight = 'notchHeight' in device ? device.notchHeight : 0
  const availableHeight = device.height - (deviceType === 'mobile' ? notchHeight : 0) - 2 * device.bezelWidth
  const availableWidth = device.width - 2 * device.bezelWidth
  
  // Scale content to fit within available space while maintaining aspect ratio
  const contentScale = Math.min(
    availableWidth / selectedRatio.width,
    availableHeight / selectedRatio.height
  )

  const contentWidth = selectedRatio.width * contentScale
  const contentHeight = selectedRatio.height * contentScale

  // Calculate centering offsets for content positioning
  // Different calculations for mobile (with notch) vs desktop
  const topOffset = deviceType === 'mobile' 
    ? notchHeight + device.bezelWidth + (availableHeight - contentHeight) / 2
    : device.bezelWidth + (availableHeight - contentHeight) / 2
  const leftOffset = device.bezelWidth + (availableWidth - contentWidth) / 2

  // Calculate safe zone dimensions if enabled
  // Safe zones are rendered as overlay indicators
  const safeZoneOverlay = safeZones && showSafeZones ? {
    top: contentHeight * safeZones.top,
    bottom: contentHeight * safeZones.bottom,
    left: contentWidth * safeZones.left,
    right: contentWidth * safeZones.right
  } : null

  // Get device label with sensible defaults
  const displayLabel = deviceLabel ?? (deviceType === 'mobile' ? 'iPhone 14 Pro' : 'Desktop Display')

  return (
    <div ref={containerRef} className={cn("flex flex-col items-center w-full", className)}>
      {/* Aspect Ratio Selector */}
      {showRatioSelectorButton && (
        <div className="relative mb-4">
          <button
            onClick={handleSelectorButtonClick}
            className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:hover:bg-gray-700 dark:focus:ring-indigo-400"
            aria-expanded={showRatioSelectorState}
            aria-haspopup="listbox"
            type="button"
          >
            {selectedRatio.ratio} ({selectedRatio.width}×{selectedRatio.height})
            <motion.span 
              className="text-gray-400"
              animate={{ rotate: showRatioSelectorState ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              ▼
            </motion.span>
          </button>

          <AnimatePresence>
            {showRatioSelectorState && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute z-10 mt-2 w-[280px] origin-top rounded-lg bg-white p-2 shadow-lg ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
                role="listbox"
              >
                {aspectRatios.map((ratio) => (
                  <button
                    key={ratio.id ?? ratio.ratio}
                    onClick={() => handleRatioSelect(ratio)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset",
                      ratio === selectedRatio
                        ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400"
                        : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    )}
                    role="option"
                    aria-selected={ratio === selectedRatio}
                    type="button"
                  >
                    <div className="flex items-center gap-2">
                      <span>
                        {ratio.ratio} ({ratio.width}×{ratio.height})
                      </span>
                      {ratio.recommended && (
                        <span className="whitespace-nowrap rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                          Recommended
                        </span>
                      )}
                    </div>
                    {ratio === selectedRatio && (
                      <RiCheckLine className="h-4 w-4 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Device Preview */}
      <div 
        className="relative flex w-full justify-center" 
        style={{ height: device.height * deviceScale }}
      >
        <div
          className={cn(
            "relative bg-gray-900 shadow-2xl",
            deviceType === 'mobile' ? 'rounded-[44px]' : 'rounded-lg'
          )}
          style={{
            width: device.width * deviceScale,
            height: device.height * deviceScale
          }}
        >
          {/* Screen Content Area */}
          <motion.div
            key={selectedRatio.id ?? selectedRatio.ratio}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
            className="absolute bg-white dark:bg-gray-800"
            style={{
              top: topOffset * deviceScale,
              left: leftOffset * deviceScale,
              width: contentWidth * deviceScale,
              height: contentHeight * deviceScale
            }}
          >
            {/* Content Container */}
            <div className="relative h-full w-full overflow-hidden">
              {/* Aspect Ratio Overlay */}
              <motion.div
                className="absolute inset-0 border-2 border-indigo-500 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: animationDuration }}
                aria-hidden="true"
              />

              {/* Safe Zone Overlay */}
              {safeZoneOverlay && (
                <motion.div
                  className="absolute border-2 border-dashed border-yellow-500/50 pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: animationDuration }}
                  style={{
                    top: safeZoneOverlay.top * deviceScale,
                    left: safeZoneOverlay.left * deviceScale,
                    right: safeZoneOverlay.right * deviceScale,
                    bottom: safeZoneOverlay.bottom * deviceScale
                  }}
                  aria-hidden="true"
                >
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-white/90 px-2 py-0.5 text-xs font-medium text-yellow-600 shadow-sm dark:bg-gray-800/90 dark:text-yellow-400">
                    Safe Zone
                  </div>
                </motion.div>
              )}

              {/* User Content */}
              <div className="relative h-full w-full">
                {children}
              </div>
            </div>
          </motion.div>

          {/* Device UI Elements */}
          {showDeviceUI && deviceType === 'mobile' && (
            <>
              {/* Notch */}
              <div 
                className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b-3xl bg-black" 
                style={{
                  width: 160 * deviceScale,
                  height: 28 * deviceScale
                }}
                aria-hidden="true"
              />
              {/* Home Indicator */}
              <div 
                className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-gray-800"
                style={{
                  width: 128 * deviceScale,
                  height: 4 * deviceScale,
                  bottom: 8 * deviceScale
                }}
                aria-hidden="true"
              />
            </>
          )}
        </div>
      </div>

      {/* Device Label */}
      <div className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-400">
        {displayLabel}
      </div>
    </div>
  )
}
